import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { LayoutGrid, Tv, Settings, LogOut, Menu, PanelLeftClose, PanelLeft, Sun, Moon, Monitor, Star, Newspaper } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useTheme } from '@/hooks/use-theme'
import { FeedbackButton } from '@/components/feedback-button'
import { Footer } from '@/components/footer'
import { WhatsNewModal } from '@/components/whats-new-modal'
import type { User } from '@/lib/api'

interface LayoutProps {
  children: React.ReactNode
  user: User
  onLogout: () => void
}

const navItems = [
  { path: '/feed', label: 'Feed', icon: LayoutGrid },
  { path: '/briefings', label: 'Briefings', icon: Newspaper },
  { path: '/library', label: 'Library', icon: Star },
  { path: '/channels', label: 'Channels', icon: Tv },
  { path: '/settings', label: 'Settings', icon: Settings },
]

const themeOptions = [
  { value: 'light' as const, icon: Sun, label: 'Light' },
  { value: 'dark' as const, icon: Moon, label: 'Dark' },
  { value: 'system' as const, icon: Monitor, label: 'System' },
]

function ThemeToggle({ collapsed }: { collapsed?: boolean }) {
  const { theme, setTheme } = useTheme()

  if (collapsed) {
    // Cycle through themes on click
    const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light'
    const current = themeOptions.find(t => t.value === theme) || themeOptions[2]
    const Icon = current.icon
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setTheme(next)}
        className="h-8 w-8"
        title={`Theme: ${current.label}`}
      >
        <Icon className="h-4 w-4" />
      </Button>
    )
  }

  return (
    <div className="flex items-center gap-1 rounded-lg bg-muted p-1">
      {themeOptions.map(({ value, icon: Icon, label }) => (
        <Button
          key={value}
          variant={theme === value ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setTheme(value)}
          className="h-7 flex-1 px-1.5"
          title={label}
        >
          <Icon className="h-3.5 w-3.5" />
        </Button>
      ))}
    </div>
  )
}

function SidebarContent({
  user,
  onLogout,
  collapsed,
  onToggle,
  onNavigate,
}: {
  user: User
  onLogout: () => void
  collapsed?: boolean
  onToggle?: () => void
  onNavigate?: () => void
}) {
  const location = useLocation()

  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex items-center justify-between">
        <Link to="/feed" className="flex items-center gap-2 px-2" onClick={onNavigate}>
          <img src="/logo.svg" alt="Rundown" className="h-8 w-8 shrink-0" />
          {!collapsed && <span className="text-lg font-bold">Rundown</span>}
        </Link>
        {onToggle && (
          <Button variant="ghost" size="icon" onClick={onToggle} className="h-8 w-8 shrink-0">
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        )}
      </div>

      <Separator className="my-4" />

      {/* Nav */}
      <nav className="flex flex-1 flex-col gap-1">
        {navItems.map(({ path, label, icon: Icon }) => {
          const isActive = location.pathname === path
          return (
            <Link key={path} to={path} onClick={onNavigate}>
              <Button
                variant={isActive ? 'secondary' : 'ghost'}
                className={collapsed ? 'w-full justify-center' : 'w-full justify-start gap-3'}
                size="sm"
                title={collapsed ? label : undefined}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {!collapsed && label}
              </Button>
            </Link>
          )
        })}
      </nav>

      {/* Theme toggle */}
      <div className={collapsed ? 'flex justify-center' : 'px-1'}>
        <ThemeToggle collapsed={collapsed} />
      </div>

      <Separator className="my-4" />

      {/* User */}
      <div className={collapsed ? 'flex flex-col items-center gap-2' : 'flex items-center gap-3 px-2'}>
        <Avatar className="h-8 w-8">
          <AvatarImage src={user.avatar_url || undefined} />
          <AvatarFallback>{(user.display_name || user.email)[0].toUpperCase()}</AvatarFallback>
        </Avatar>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-medium">{user.display_name || user.email}</p>
            {user.display_name && (
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            )}
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onLogout}
          className="h-8 w-8 shrink-0"
          title="Log out"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

export function Layout({ children, user, onLogout }: LayoutProps) {
  const location = useLocation()
  // The video detail page uses a two-column layout (summary + related rail),
  // which needs more room than the default narrow reading width. Other pages
  // stay at the comfortable max-w-3xl prose width.
  const isWide = location.pathname.startsWith('/videos/')
  const [sheetOpen, setSheetOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('sidebar-collapsed') === 'true'
  })

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(collapsed))
  }, [collapsed])

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile: top bar with hamburger + sheet drawer */}
      <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur-lg md:hidden">
        <div className="flex h-14 items-center justify-between px-4">
          <Link to="/feed" className="flex items-center gap-2">
            <img src="/logo.svg" alt="Rundown" className="h-8 w-8" />
            <span className="text-lg font-bold">Rundown</span>
          </Link>

          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-4">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation</SheetTitle>
              </SheetHeader>
              <SidebarContent user={user} onLogout={onLogout} onNavigate={() => setSheetOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Desktop: fixed collapsible sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 hidden border-r bg-background p-4 transition-[width] duration-200 md:flex md:flex-col ${
          collapsed ? 'w-16' : 'w-56'
        }`}
      >
        <SidebarContent
          user={user}
          onLogout={onLogout}
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
        />
      </aside>

      {/* Content */}
      <main className={`transition-[padding] duration-200 ${collapsed ? 'md:pl-16' : 'md:pl-56'}`}>
        <div className={`mx-auto flex min-h-screen flex-col px-4 py-6 ${isWide ? 'max-w-6xl' : 'max-w-3xl'}`}>
          <div className="flex-1">
            {children}
          </div>
          <Footer />
        </div>
      </main>

      <FeedbackButton />
      <WhatsNewModal />
    </div>
  )
}
